import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AuthCallbackPage — handles the OAuth redirect from Cognito Hosted UI.
 *
 * Account-linking (AL-M3, F4/F5): when an existing native (password) user tries
 * Google, PreSignUp throws `LINK_REQUIRED:<requestId>`. Cognito puts it in the
 * callback URL's `error_description`, but Amplify processes + strips OAuth params
 * on init before this component reads them. So `configureAuth()` captures the
 * requestId into sessionStorage at app boot; here we read it (with URL + Hub
 * fallbacks) and route to `/auth/link-confirm?req=<id>`.
 *
 * Spec: docs/AL-M3/DESIGN.md §4.3, infra/lambda/pre-sign-up/index.ts.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hub } from "aws-amplify/utils";
import { useAuth } from "../AuthProvider.js";
const LINK_REQUIRED_RE = /LINK_REQUIRED:([0-9a-fA-F-]{36})/;
const STASH_KEY = "al_pending_link_req";
function extractLinkRequestId(description) {
    if (!description)
        return null;
    const match = LINK_REQUIRED_RE.exec(description);
    return match ? match[1] : null;
}
/** Read a stashed requestId (set by configureAuth before Amplify cleared the URL). */
function readStashedRequestId() {
    try {
        return sessionStorage.getItem(STASH_KEY);
    }
    catch {
        return null;
    }
}
/** Fallback: read error_description from the current URL (query or hash). */
function errorFromUrl() {
    const fromSearch = new URLSearchParams(window.location.search).get("error_description");
    if (fromSearch)
        return fromSearch;
    const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
    return new URLSearchParams(hash).get("error_description");
}
export function AuthCallbackPage({ wrapper: Wrapper, linkConfirmPath = "/auth/link-confirm", }) {
    const navigate = useNavigate();
    const { refreshSession } = useAuth();
    const [statusMessage, setStatusMessage] = useState("Completing secure sign-in.");
    useEffect(() => {
        let active = true;
        const goToLinkConfirm = (requestId) => {
            if (!active)
                return;
            setStatusMessage("This email already has an account — let's link it.");
            navigate(`${linkConfirmPath}?req=${encodeURIComponent(requestId)}`, {
                replace: true,
            });
        };
        // 1. Stashed by configureAuth (most reliable — captured before Amplify
        //    stripped the URL), then URL, then Hub failure event.
        const requestId = readStashedRequestId() ?? extractLinkRequestId(errorFromUrl());
        if (requestId) {
            goToLinkConfirm(requestId);
            return;
        }
        // 2. Late Hub failure event fallback.
        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            if (payload.event === "signInWithRedirect_failure") {
                const data = payload.data;
                const id = readStashedRequestId() ?? extractLinkRequestId(data?.error?.message);
                if (id)
                    goToLinkConfirm(id);
            }
        });
        // 3. Normal sign-in completion.
        const finalize = async () => {
            await refreshSession();
            if (active &&
                !readStashedRequestId() &&
                !extractLinkRequestId(errorFromUrl())) {
                navigate("/", { replace: true });
            }
        };
        void finalize();
        return () => {
            active = false;
            unsubscribe();
        };
    }, [navigate, refreshSession, linkConfirmPath]);
    const content = (_jsx("div", { className: "flex min-h-[calc(100vh-128px)] items-center justify-center", children: _jsxs("div", { className: "space-y-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm", children: [_jsx("h1", { className: "text-2xl font-semibold text-slate-900", children: "Signing you in..." }), _jsx("p", { className: "text-sm text-slate-600", children: statusMessage })] }) }));
    return Wrapper ? _jsx(Wrapper, { children: content }) : content;
}
